import { useEffect, useRef, useState } from 'react';
import { CalendarDays, FileDown, FileText, Loader2, Printer } from 'lucide-react';
import { useDocxExport } from '@skills/docx-export.jsx';
import { usePdfExport } from '@skills/pdf-export.jsx';
import { getStaffPass } from '@generated/hooks/staffPass';
import { getCommunityCalendar, lookupVenueEvents } from '@generated/server/communityCalendar';
import { preloadLogo } from '@generated/components/logoDocx';
import { RHSR_LOGO, VSR_LOGO } from '@generated/components/AdGuide';
import { cleanLink, dayHeading } from '@generated/components/sceneGuideDocx';
import { addDays, nyToday, rangeTitle } from '@generated/components/sceneRange';
import { PAPER_NAME, type CalPaper } from '@generated/components/communitySources';
import { buildCommunityDocx, placeLine, type CalItem, type CommunityCal } from '@generated/components/communityDocx';

const monthRange = (offset: number) => {
  const [y, m] = nyToday().split('-').map(Number);
  const s = new Date(Date.UTC(y ?? 2026, (m ?? 1) - 1 + offset, 1, 12)), e = new Date(Date.UTC(y ?? 2026, (m ?? 1) + offset, 0, 12));
  return { from: s.toISOString().slice(0, 10), to: e.toISOString().slice(0, 10) };
};
const PRESETS = [
  { label: 'This Month', range: () => monthRange(0) },
  { label: 'Next Month', range: () => monthRange(1) },
  { label: 'Next 14 Days', range: () => ({ from: nyToday(), to: addDays(nyToday(), 13) }) },
];
const btn = 'inline-flex min-h-11 items-center gap-2 border border-foreground px-4 text-sm font-semibold disabled:opacity-50';

function Listing({ e, showWhen }: { e: CalItem; showWhen?: boolean }) {
  return (
    <article className="break-inside-avoid pt-4">
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary">{e.neighborhood}</p>
      <h5 className="font-[family-name:var(--font-heading)] text-lg font-semibold leading-snug">{e.title}</h5>
      {showWhen && e.when && <p className="text-sm font-semibold">{e.when}</p>}
      {placeLine(e) && <p className="text-sm">{placeLine(e)}</p>}
      {e.cost && <p className="text-sm italic">{e.cost}</p>}
      {e.excerpt && <p className="mt-1 text-sm leading-relaxed">“{e.excerpt}”</p>}
      <p className="mt-0.5 break-all text-xs text-muted-foreground">Presented by {e.org}{e.link?.startsWith('http') && <> · <a href={e.link} target="_blank" rel="noreferrer" className="underline">{cleanLink(e.link)}</a></>}</p>
    </article>
  );
}

/** Reader-facing Community Calendar from neighborhood organization sites, ready for the paper. */
export function CommunityCalendar() {
  const [paper, setPaper] = useState<CalPaper>('village');
  const [range, setRange] = useState(monthRange(1));
  const [c, setC] = useState<CommunityCal | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [looking, setLooking] = useState<{ done: number; total: number; org: string } | null>(null);
  const docRef = useRef<HTMLDivElement>(null);
  const { exportToDocx, isExporting: wordBusy } = useDocxExport();
  const { exportToPdf, isExporting: pdfBusy } = usePdfExport({ pageSize: 'Letter', orientation: 'portrait', margin: 36 });
  useEffect(() => { void preloadLogo(); }, []);

  const valid = !!range.from && !!range.to && range.from <= range.to;
  const run = async () => {
    if (!valid) return;
    setBusy(true); setError(null);
    let r: CommunityCal | null = null;
    try { r = (await getCommunityCalendar({ data: { pass: getStaffPass(), paper, ...range } })) ?? null; setC(r); }
    catch (e) { console.error(e); setError('The calendar could not be built just now — this is usually a brief connection hiccup. Please press Build again in a moment.'); }
    finally { setBusy(false); }
    if (!r?.lookup?.length) return;
    // Look up venues whose own pages showed no events, two at a time, adding results as they arrive.
    const queue = [...r.lookup];
    const total = queue.length;
    let done = 0;
    const key = (e: CalItem) => `${e.title.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 24)}|${e.startsAt.slice(0, 10)}`;
    const byDate = (a: CalItem, b: CalItem) => (a.startsAt || '9999').localeCompare(b.startsAt || '9999');
    const worker = async () => {
      while (queue.length) {
        const org = queue.shift()!;
        setLooking({ done, total, org });
        try {
          const res = await lookupVenueEvents({ data: { pass: getStaffPass(), org, from: r!.from, to: r!.to } });
          const found = res?.events ?? [];
          const trads = res?.traditions ?? [];
          setC((cur) => {
            if (!cur) return cur;
            const seen = new Set([...cur.events, ...cur.entertainment].map(key));
            const fresh = found.filter((e) => !seen.has(key(e)));
            const tSeen = new Set(cur.traditions.map((e) => e.title.toLowerCase()));
            return { ...cur,
              events: [...cur.events, ...fresh.filter((e) => e.category !== 'entertainment')].sort(byDate),
              entertainment: [...cur.entertainment, ...fresh.filter((e) => e.category === 'entertainment')].sort(byDate),
              traditions: [...cur.traditions, ...trads.filter((e) => !tSeen.has(e.title.toLowerCase()))],
              read: res?.read ? [...cur.read, org] : cur.read,
              failed: res?.read ? cur.failed : [...cur.failed, org] };
          });
        } catch (e) { console.error('lookup', org, e); }
        done++;
        setLooking({ done, total, org });
      }
    };
    await Promise.all([worker(), worker()]);
    setLooking(null);
  };
  const built = c ? { from: c.from, to: c.to } : range;
  const title = rangeTitle(built.from, built.to).replace(/ Guide$/, '');
  const paperName = PAPER_NAME[c?.paper ?? paper];
  const fileBase = `community-calendar_${c?.paper ?? paper}_${built.from}_to_${built.to}`;
  const stale = !!c && (c.from !== range.from || c.to !== range.to || c.paper !== paper);
  const print = () => {
    const node = docRef.current; if (!node) return;
    const w = window.open('', '_blank', 'width=900,height=1000'); if (!w) { window.print(); return; }
    const styles = [...document.querySelectorAll('style, link[rel="stylesheet"]')].map((x) => x.outerHTML).join('');
    w.document.write(`<!doctype html><html class="${document.documentElement.className}"><head><title>Community Calendar · ${title}</title>${styles}<style>@page{margin:.5in}body{background:#fff}</style></head><body>${node.outerHTML}</body></html>`);
    w.document.close(); setTimeout(() => { w.focus(); w.print(); }, 600);
  };

  let lastDay = '';
  let lastEntDay = '';
  return (
    <div>
      <div className="border-2 border-foreground p-4">
        <div className="flex flex-wrap gap-2" role="group" aria-label="Paper">
          {(['village', 'redhook'] as const).map((pp) => <button key={pp} type="button" aria-pressed={paper === pp} onClick={() => setPaper(pp)} className={`min-h-11 border px-3 text-sm font-semibold italic ${paper === pp ? 'border-foreground bg-foreground text-background' : 'border-border'}`}>{PAPER_NAME[pp]}</button>)}
        </div>
        <p className="mt-2 text-xs text-muted-foreground">{paper === 'village' ? 'East Village, West Village, Greenwich Village, SoHo and Tribeca organizations.' : 'Red Hook and Columbia Waterfront organizations.'}</p>
        <div className="mt-3 flex flex-wrap gap-2">{PRESETS.map((pr) => { const r = pr.range(); const on = r.from === range.from && r.to === range.to; return <button key={pr.label} type="button" aria-pressed={on} onClick={() => setRange(r)} className={`min-h-11 border px-3 text-sm font-semibold ${on ? 'border-foreground bg-foreground text-background' : 'border-border hover:border-foreground'}`}>{pr.label}</button>; })}</div>
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <label className="text-sm">From<input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} className="mt-1 block min-h-11 border border-input bg-background px-2" /></label>
          <label className="text-sm">To<input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} className="mt-1 block min-h-11 border border-input bg-background px-2" /></label>
          <span className="pb-3 text-xs text-muted-foreground">Custom date range</span>
        </div>
        {stale && <p className="mt-2 text-sm text-destructive">The paper or dates changed. Build the calendar again to refresh it.</p>}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" onClick={() => void run()} disabled={busy || !valid} className="inline-flex min-h-11 items-center gap-2 bg-primary px-5 text-sm font-semibold text-primary-foreground disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarDays className="h-4 w-4" />}Build the calendar</button>
        {c && <>
          <button type="button" className={btn} disabled={wordBusy} onClick={() => void exportToDocx(buildCommunityDocx(c, paperName, title), `${fileBase}.docx`)}>{wordBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}Word file</button>
          <button type="button" className={btn} disabled={pdfBusy} onClick={() => void exportToPdf(docRef, `${fileBase}.pdf`)}>{pdfBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}PDF</button>
          <button type="button" className={btn} onClick={print}><Printer className="h-4 w-4" />Print</button>
        </>}
      </div>
      {looking && <p className="mt-4 flex items-center gap-2 text-sm"><Loader2 className="h-4 w-4 animate-spin" />Reading sources ({looking.done} of {looking.total}): {looking.org}… new events appear below as they're found.</p>}
      {busy && !c && <p className="mt-4 text-sm text-muted-foreground">Reading the neighborhood websites… this can take up to a minute.</p>}
      {error && <p role="alert" className="mt-4 text-sm text-destructive">{error}</p>}
      {c && !c.liveOk && <p className="mt-3 text-sm font-semibold">Live websites could not be read right now, so this calendar shows the verified standing listings only. Build again later for newly announced events.</p>}
      {c && !looking && c.failed.length > 0 && <p className="mt-3 text-xs text-muted-foreground">Couldn’t read: {c.failed.join(', ')}.</p>}
      {c && (
        <div ref={docRef} className={`mt-6 bg-[hsl(var(--paper))] p-6 sm:p-10 ${busy ? 'opacity-60' : ''}`}>
          <header className="flex items-start justify-between gap-4 border-b-4 border-double border-foreground pb-3">
            <div className="min-w-0">
              <h3 className="font-[family-name:var(--font-heading)] text-4xl font-semibold tracking-tight sm:text-5xl">Community Calendar</h3>
              <p className="mt-1 text-sm italic">{paperName}</p>
              <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{title}</p>
            </div>
            <span className="flex shrink-0 gap-2"><img src={RHSR_LOGO} alt="Red Hook Star-Revue" crossOrigin="anonymous" className="h-14 w-14 object-contain" /><img src={VSR_LOGO} alt="The Village Star-Revue" crossOrigin="anonymous" className="h-14 w-14 object-contain" /></span>
          </header>
          {c.events.length === 0 && <p className="mt-6 text-sm italic">No dated community events were listed for these dates.</p>}
          <div className="mt-2 sm:columns-2 sm:gap-10">
            {c.events.map((e, i) => { const day = dayHeading(e.startsAt); const show = day !== lastDay; lastDay = day; return (
              <div key={`${e.title}-${i}`} className="break-inside-avoid">{show && <h4 className="mt-4 border-b-2 border-foreground pb-1 text-xs font-bold uppercase tracking-[0.18em] text-primary">{day}</h4>}<Listing e={e} /></div>
            ); })}
          </div>
          {c.traditions.length > 0 && <section className="mt-8 border-t-4 border-double border-foreground pt-3">
            <h4 className="font-[family-name:var(--font-heading)] text-2xl font-semibold">Standing Weekly Community Traditions</h4>
            <div className="sm:columns-2 sm:gap-10">{c.traditions.map((e, i) => <Listing key={`t-${e.title}-${i}`} e={e} showWhen />)}</div>
          </section>}
          <section className="mt-10 border-t-[6px] border-double border-foreground pt-4">
            <h3 className="font-[family-name:var(--font-heading)] text-4xl font-semibold tracking-tight sm:text-5xl">Entertainment Calendar</h3>
            <p className="mt-1 text-xs uppercase tracking-[0.18em] text-muted-foreground">Music · Comedy · Theater · Dance · Film · Museums · Libraries · {title}</p>
            {(c.entertainment ?? []).length === 0 && <p className="mt-4 text-sm italic">No dated shows were listed for these dates.</p>}
            <div className="mt-2 sm:columns-2 sm:gap-10">
              {(c.entertainment ?? []).map((e, i) => { const day = dayHeading(e.startsAt); const show = day !== lastEntDay; lastEntDay = day; return (
                <div key={`ent-${e.title}-${i}`} className="break-inside-avoid">{show && <h4 className="mt-4 border-b-2 border-foreground pb-1 text-xs font-bold uppercase tracking-[0.18em] text-primary">{day}</h4>}<Listing e={e} /></div>
              ); })}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
